import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  differenceInCalendarDays,
  format,
  getISOWeek,
  getISOWeekYear,
  getQuarter,
  isValid,
  max,
  parseISO,
  startOfDay,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns';

export const GRANULARITIES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

export const PERIODS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'custom', label: 'Custom' },
];

const DEFAULT_PERIOD = 'week';

export const DATA_START = new Date(2026, 9, 1);

export const toApiDate = (date) => format(date, 'yyyy-MM-dd');

const parseDate = (value) => {
  if (!value) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

const isGranularity = (value) => GRANULARITIES.some((g) => g.value === value);

export const bucketStart = (date, granularity) => {
  switch (granularity) {
    case 'week':
      return startOfISOWeek(date);
    case 'month':
      return startOfMonth(date);
    case 'quarter':
      return startOfQuarter(date);
    default:
      return startOfDay(date);
  }
};

const addBuckets = (date, granularity, amount) => {
  switch (granularity) {
    case 'week':
      return addWeeks(date, amount);
    case 'month':
      return addMonths(date, amount);
    case 'quarter':
      return addQuarters(date, amount);
    default:
      return addDays(date, amount);
  }
};

const presetStart = (granularity, to) => {
  switch (granularity) {
    case 'day':
      return subDays(to, 29);
    case 'month':
      return startOfMonth(subMonths(to, 11));
    case 'quarter':
      return startOfQuarter(subQuarters(to, 7));
    default:
      return startOfISOWeek(subWeeks(to, 11));
  }
};

export const presetRange = (granularity, today = new Date()) => {
  const to = startOfDay(today);
  return { from: max([presetStart(granularity, to), DATA_START]), to };
};

export const autoGranularity = (from, to) => {
  const days = differenceInCalendarDays(to, from) + 1;
  if (days <= 62) return 'day';
  if (days <= 183) return 'week';
  if (days <= 1096) return 'month';
  return 'quarter';
};

export const resolveFilters = (searchParams, tabs, today = new Date()) => {
  const tabParam = searchParams.get('tab');
  const tab = tabs.some((t) => t.value === tabParam) ? tabParam : tabs[0].value;

  const periodParam = searchParams.get('period');
  const period = PERIODS.some((p) => p.value === periodParam) ? periodParam : DEFAULT_PERIOD;
  const unique = searchParams.get('unique') === '1';

  if (period !== 'custom') {
    const { from, to } = presetRange(period, today);
    return { tab, period, granularity: period, from: toApiDate(from), to: toApiDate(to), unique };
  }

  const fallback = presetRange(DEFAULT_PERIOD, today);
  const from = max([parseDate(searchParams.get('from')) || fallback.from, DATA_START]);
  const to = max([parseDate(searchParams.get('to')) || fallback.to, from]);
  const granularityParam = searchParams.get('granularity');
  const granularity = isGranularity(granularityParam) ? granularityParam : autoGranularity(from, to);

  return { tab, period, granularity, from: toApiDate(from), to: toApiDate(to), unique };
};

export const formatPeriod = (value, granularity) => {
  const date = parseISO(value);
  switch (granularity) {
    case 'week':
      return `W${getISOWeek(date)} ${getISOWeekYear(date)}`;
    case 'month':
      return format(date, 'MMM yyyy');
    case 'quarter':
      return `Q${getQuarter(date)} ${format(date, 'yyyy')}`;
    default:
      return format(date, 'd MMM');
  }
};

export const nextPeriods = (value, granularity, count) =>
  Array.from({ length: count }, (_, i) => toApiDate(addBuckets(parseISO(value), granularity, i + 1)));

export const isCurrentPeriod = (value, granularity, today = new Date()) =>
  value === toApiDate(bucketStart(today, granularity));

export const isPartialPeriod = (value) => value < toApiDate(DATA_START);
